// Clinical status options for conditions
export type ClinicalStatus = 'active' | 'resolved' | 'remission';

// Date precision levels for flexible date input
export type DatePrecision = 'year' | 'month' | 'day';

// Flexible date structure supporting partial dates
export interface FlexibleDate {
  value: string; // ISO format: '2015', '2015-05', or '2015-05-12'
  precision: DatePrecision;
}

// ===== LOCAL FORM INTERFACES (UI State) =====

export interface ActiveConditionLocal {
  localId: string; // UUID for React key
  fhirId?: string; // Condition/{id} if exists in backend
  conditionName: string; // Patient's words
  onsetDate: FlexibleDate | null;
  comments?: string; // Free-text patient comments
  isDeleted?: boolean; // Soft delete flag for save logic
}

export interface PriorEventLocal {
  localId: string;
  fhirId?: string;
  eventName: string;
  eventDate: FlexibleDate | null;
  status: 'resolved' | 'remission';
  comments?: string; // Additional details from patient
  isDeleted?: boolean;
}

export interface ProcedureLocal {
  localId: string;
  fhirId?: string;
  procedureName: string;
  procedureDate: FlexibleDate | null;
  reasonConditionIds?: string[]; // FHIR references to existing Conditions
  comments?: string; // Free-text patient comments
  isDeleted?: boolean;
}

export interface MedicalHistoryData {
  activeConditions: ActiveConditionLocal[];
  priorEvents: PriorEventLocal[];
  procedures: ProcedureLocal[];
}

// ===== FHIR CONSTANTS =====

export const CLINICAL_STATUS_CODES = {
  active: {
    system: 'http://terminology.hl7.org/CodeSystem/condition-clinical',
    code: 'active',
    display: 'Active',
  },
  resolved: {
    system: 'http://terminology.hl7.org/CodeSystem/condition-clinical',
    code: 'resolved',
    display: 'Resolved',
  },
  remission: {
    system: 'http://terminology.hl7.org/CodeSystem/condition-clinical',
    code: 'remission',
    display: 'Remission',
  },
} as const;

export const VERIFICATION_STATUS = {
  unconfirmed: {
    system: 'http://terminology.hl7.org/CodeSystem/condition-ver-status',
    code: 'unconfirmed',
    display: 'Unconfirmed',
  },
} as const;

export const CONDITION_CATEGORY = {
  problemListItem: {
    system: 'http://terminology.hl7.org/CodeSystem/condition-category',
    code: 'problem-list-item',
    display: 'Problem List Item',
  },
} as const;

export const PROCEDURE_STATUS = 'completed' as const;
